/**
 * Sweet Listeners sitewide palette — one pink, one blue, no drift.
 *
 * Locked hexes:
 *   Pink CTAs / Sweet wordmark / talker accents: #F51F79
 *   Blue CTAs / Listeners wordmark / listener accents: #159BE8
 *   Page background: #FFFDFC
 *   Body / nav text: #0B2B5B
 *   Tagline: #516281
 *   Talker card wash: #FFF1F6 → #FFF9FB
 *   Listener card wash: #EEF8FF → #F7FBFF
 *
 * Never charcoal #2D2E2F / #1F2021 or raw black for text. Never teal.
 * Never electric clinic blue. Never washed peach. Never soft sky #7BA7C2.
 */
export const BRAND_CREAM = '#FFFDFC';
export const BRAND_CREAM_CENTER = '#FFFFFF';
export const BRAND_HOME_NAVY = '#0B2B5B';
export const BRAND_HOME_PINK = '#F51F79';
export const BRAND_HOME_BLUE = '#159BE8';
export const BRAND_TALKER_WASH_START = '#FFF1F6';
export const BRAND_TALKER_WASH_END = '#FFF9FB';
export const BRAND_LISTENER_WASH_START = '#EEF8FF';
export const BRAND_LISTENER_WASH_END = '#F7FBFF';
export const BRAND_PEACH = '#FFF1F6';
export const BRAND_BLUSH = '#FFF1F6';
export const BRAND_BLUSH_MID = '#FFF9FB';
export const BRAND_PINK = '#F51F79';
/** Official ear-heart inner curves — same locked pink as CTAs and Sweet. */
export const BRAND_LOGO_INNER_EAR = '#F51F79';
/** Lockup wordmark — Sweet pink, Listeners blue. */
export const BRAND_WORDMARK_SWEET = '#F51F79';
export const BRAND_WORDMARK_LISTENERS = '#159BE8';
/** Lockup tagline listen . care . connect . */
export const BRAND_WORDMARK_TAGLINE = '#516281';
/** Official lockup PNG is 872×681. Wordmark ends ~583; tagline starts ~630. */
export const LOCKUP_PNG_WIDTH = 872;
export const LOCKUP_PNG_HEIGHT = 681;
export const LOCKUP_WORDMARK_HEIGHT = 600;
export const LOCKUP_TAGLINE_COPY = 'Listen. Care. Connect. Learn.';
export const BRAND_PINK_HOVER = '#D41868';
export const BRAND_ROSE = '#F51F79';
export const BRAND_ROSE_HOVER = '#D41868';
export const BRAND_DUSTY_ROSE = '#F51F79';
export const BRAND_BLUSH_PUNCH = '#F51F79';
export const BRAND_HONEY = '#FFF9FB';
export const BRAND_CTA = '#F51F79';
export const BRAND_CTA_HOVER = '#D41868';
export const BRAND_SECONDARY_TEXT = '#3D4F66';
export const BRAND_INK = '#0B2B5B';
export const BRAND_CHARCOAL = '#0B2B5B';
export const BRAND_CHARCOAL_DARK = '#0B2B5B';
export const BRAND_TAUPE = '#516281';
export const BRAND_TAUPE_SOFT = '#516281';
export const BRAND_BORDER = '#E8EEF4';
export const BRAND_INPUT_BORDER = '#D4DDE6';
export const BRAND_SUCCESS = '#138041';
export const BRAND_ERROR = '#E7000B';
export const FORBIDDEN_CHARCOAL = '#2D2E2F';
export const FORBIDDEN_CHARCOAL_DARK = '#1F2021';
export const BRAND_SKY = '#EEF8FF';
/** Locked listener / sky accent — same as homepage CSS and Listeners wordmark. */
export const BRAND_SKY_DEEP = '#159BE8';
export const BRAND_ACCENT = '#159BE8';

export const FORBIDDEN_TEAL = '#4E8580';
export const FORBIDDEN_ELECTRIC_BLUE = '#3B82F6';
export const FORBIDDEN_WASHED_PEACH = '#E8A0A4';
export const FORBIDDEN_SOFT_SKY = '#7BA7C2';
export const FORBIDDEN_OLD_RASPBERRY = '#E82878';

export const OFFICIAL_MARKETPLACE_COLORS = {
  mainColor: BRAND_CTA,
  primaryButton: BRAND_CTA,
  notificationPrimaryButton: BRAND_CTA,
};

/**
 * CSS custom properties applied to <html> and #page so Search, Dashboard,
 * Profile, Help, Account, Inbox, buttons, links, and badges inherit one system.
 * Hosted Console colors never win these keys.
 */
export const LOCKED_CSS_PROPERTIES = {
  '--marketplaceColor': BRAND_HOME_PINK,
  '--marketplaceColorDark': BRAND_CTA_HOVER,
  '--marketplaceColorLight': BRAND_TALKER_WASH_START,
  '--colorPrimaryButton': BRAND_HOME_PINK,
  '--colorPrimaryButtonDark': BRAND_CTA_HOVER,
  '--colorPrimaryButtonLight': BRAND_TALKER_WASH_START,
  '--colorAccent': BRAND_HOME_BLUE,
  '--colorSkyDeep': BRAND_HOME_BLUE,
  '--colorLinkSecondary': BRAND_HOME_BLUE,
  '--colorRose': BRAND_HOME_PINK,
  '--colorDustyRose': BRAND_HOME_PINK,
  '--colorBlushPunch': BRAND_HOME_PINK,
  '--homePink': BRAND_HOME_PINK,
  '--homeBlue': BRAND_HOME_BLUE,
  '--homeNavy': BRAND_HOME_NAVY,
  '--navy': BRAND_HOME_NAVY,
  '--pink': BRAND_HOME_PINK,
  '--blue': BRAND_HOME_BLUE,
  '--homeTagline': BRAND_WORDMARK_TAGLINE,
  '--colorCream': BRAND_CREAM,
  '--colorGrey50': BRAND_CREAM,
  '--colorBlack': BRAND_HOME_NAVY,
  '--colorInk': BRAND_HOME_NAVY,
  '--colorCharcoal': BRAND_HOME_NAVY,
  '--colorCharcoalDark': BRAND_HOME_NAVY,
  '--colorTaupe': BRAND_WORDMARK_TAGLINE,
  '--colorTaupeSoft': BRAND_WORDMARK_TAGLINE,
  '--colorGrey600': BRAND_WORDMARK_TAGLINE,
  '--colorGrey700': BRAND_SECONDARY_TEXT,
  '--colorGrey900': BRAND_HOME_NAVY,
  '--colorSuccess': BRAND_SUCCESS,
  '--colorFail': BRAND_ERROR,
};

export const isForbiddenTeal = hex => {
  if (!hex || typeof hex !== 'string') {
    return false;
  }
  return hex.replace('#', '').toLowerCase() === '4e8580';
};

/**
 * Console branding.json still ships teal. Replace those colors so they
 * never reach CSS, SSR HTML, or the Redux dump.
 */
export const overwriteHostedMarketplaceColors = (assetName, data) => {
  if (assetName !== 'branding' || !data || typeof data !== 'object') {
    return data;
  }
  return {
    ...data,
    marketplaceColors: { ...OFFICIAL_MARKETPLACE_COLORS },
  };
};
